import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { generateArticleSlug } from '../../i18n/utils/slugGenerator';
import ErrorState from '../ErrorState/ErrorState';
import BlogListSkeleton from './../BlogSection/BlogListSkeleton';
import { useCachedFetch } from '../../hooks/useCachedFetch';
import { IMAGES_BASE_URL } from '../../config/constants';
import './../BlogSection/BlogList.scss';

const BlogContent = () => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;

    const { categorySlug } = useParams();
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [visibleCount, setVisibleCount] = useState(12);
    const category = categorySlug || 'all';

    const { 
        data: mainData, 
        loading: mainDataLoading, 
        error: mainDataError, 
        refetch: refetchMainData 
    } = useCachedFetch('/data/locales/mainData.json');
    
    const { 
        data: articlesData, 
        loading: articlesLoading, 
        error: articlesError, 
        refetch: refetchArticlesData 
    } = useCachedFetch('/data/locales/articles.json');

    const handleRetry = useCallback(() => {
        refetchMainData();
        refetchArticlesData();
    }, [refetchMainData, refetchArticlesData]);

    const mainContent = useMemo(() => {
        return mainData?.TopMainData?.BlogListSection;
    }, [mainData]);

    const articles = useMemo(() => {
        return articlesData?.ArticleData || [];
    }, [articlesData]);

    const isLoading = mainDataLoading || articlesLoading;
    const hasError = mainDataError || articlesError || !mainContent || articles.length === 0;

    useEffect(() => {
        setVisibleCount(12);
    }, [categorySlug]);

    const getLocalizedTitle = useCallback((titleObj) => {
        return typeof titleObj === 'object'
            ? titleObj[currentLang] || titleObj.en
            : titleObj;
    }, [currentLang]);

    const toggleSearch = useCallback(() => setShowSearch((prev) => !prev), []);

    const uniqueCategories = useMemo(() => {
        return [...new Set(articles.map((article) => article.webLink?.category?.slug).filter(Boolean))];
    }, [articles]);

    const handleSearchChange = useCallback((e) => {
        setSearchQuery(e.target.value);
    }, []);

    const handleSearchSubmit = useCallback((e) => {
        e.preventDefault();
    }, []);

    const filteredArticles = useMemo(() => {
        if (!articles || !Array.isArray(articles)) {
            return [];
        }

        const filtered = articles.filter((article) => {
            if (!article.webLink?.category?.slug) {
                return false;
            }

            const articleTitle = typeof article.mainArticleTitle === 'object'
                ? article.mainArticleTitle[currentLang] || article.mainArticleTitle.en
                : article.mainArticleTitle;

            const matchesCategory = category === 'all' || article.webLink.category.slug === category;
            const matchesSearch = searchQuery === '' ||
                (articleTitle && articleTitle.toLowerCase().includes(searchQuery.toLowerCase()));

            return matchesCategory && matchesSearch;
        });

        return filtered.sort((a, b) => (b.id || 0) - (a.id || 0));
    }, [articles, category, searchQuery, currentLang]);

    const loadMoreArticles = useCallback(() => {
        setVisibleCount((prevCount) => Math.min(prevCount + 12, filteredArticles.length));
    }, [filteredArticles.length]);

    if (isLoading) {
        return <BlogListSkeleton />;
    }

    if (hasError || !mainContent) {
        return <ErrorState onRetry={handleRetry} />;
    }

    return (
        <div className="blog__list">
            <div className="blog__filters">
                <div className="categories">
                    <Link
                        to="/blog"
                        className={`category__link ${category === 'all' ? 'active' : ''}`}
                    >
                        {mainContent.button.text?.[currentLang] || mainContent.button.text?.en || ''}
                    </Link>
                    {uniqueCategories.map((slug) => {
                        const categoryItem = articles.find(
                            (article) => article.webLink?.category?.slug === slug
                        )?.webLink?.category;

                        if (!categoryItem) return null;

                        return (
                            <Link
                                key={slug}
                                to={`/blog/${slug}`}
                                className={`category__link ${category === slug ? 'active' : ''}`}
                            >
                                {typeof categoryItem.categoryName === 'object'
                                    ? categoryItem.categoryName[currentLang] || categoryItem.categoryName.en
                                    : categoryItem.categoryName
                                }
                            </Link>
                        );
                    })}
                    <button
                        className="search__button"
                        onClick={toggleSearch}
                        type="button"
                        aria-label={showSearch ? 'Close search' : 'Open search'}
                    >
                        <FontAwesomeIcon
                            className={`lupa ${showSearch ? 'active' : ''}`}
                            icon={faMagnifyingGlass}
                        />
                    </button>
                </div>
            </div>

            <div className={`search__popup ${showSearch ? 'active' : ''}`}>
                <form onSubmit={handleSearchSubmit} className="search__form">
                    <input
                        id="blog-search-input"
                        name="blog-search"
                        className="search__popup__input"
                        type="text"
                        placeholder={mainContent.searchPlaceholder?.[currentLang] || mainContent.searchPlaceholder?.en || ''}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        aria-label="Search articles"
                    />
                </form>
                {category !== 'all' && (
                    <>
                        <p className="search__popup__hint">
                            {mainContent.firstSearchContent?.[currentLang] || mainContent.firstSearchContent?.en || ''}
                        </p>
                        <p className="search__popup__hint">
                            {mainContent.secondSearchContent?.[currentLang] || mainContent.secondSearchContent?.en || ''}
                        </p>
                    </>
                )}
            </div>

            <div className="articles">
                {filteredArticles.length === 0 ? (
                    <div className="no__articles">
                        <p className="no__articles__message">
                            {searchQuery === ''
                                ? mainContent.noArticlesMessage?.[currentLang] || mainContent.noArticlesMessage?.en || ''
                                : mainContent.noResultsMessage?.[currentLang] || mainContent.noResultsMessage?.en || ''}
                        </p>
                    </div>
                ) : (
                    <div className="article__grid">
                        {filteredArticles.slice(0, visibleCount).map((article, index) => (
                            <div className="article__card" key={`${article.id}-${index}`}>
                                <Link
                                    to={`/blog/${article.webLink.category.slug}/${generateArticleSlug(article.mainArticleTitle, currentLang)}`}
                                    className="article__link"
                                >
                                    <div className="article__image">
                                        <img
                                            className="article__image__pic"
                                            src={`${IMAGES_BASE_URL}${article.articleImage}`}
                                            alt={getLocalizedTitle(article.mainArticleTitle)}
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="article__info">
                                        <p className="article__date">
                                            {typeof article.dateArticle === 'object'
                                                ? article.dateArticle[currentLang] || article.dateArticle.en
                                                : article.dateArticle
                                            }
                                        </p>
                                        <h3 className="article__title">
                                            {getLocalizedTitle(article.mainArticleTitle)}
                                        </h3>
                                    </div>
                                </Link>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {filteredArticles.length > visibleCount && (
                <button
                    className="load__more"
                    onClick={loadMoreArticles}
                    type="button"
                >
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    {mainContent.loadArticles?.[currentLang] || mainContent.loadArticles?.en || ''}
                </button>
            )}
        </div>
    );
};

export default React.memo(BlogContent);